<template>
  <div class="fts-listing-actions">
    <div class="listing-actions-container">
      <!-- create -->
      <fts-btn-hold
        tag="a"
        :href="getCreateActivityLink"
        color="primary"
        text-transform-init
        box-shadow-none
        class="fts-font-normal"
        @clickEvent="executeCreateActivity()"
        @longPressEvent="executeCreateActivity(true)"
        v-if="isCreateRight"
      >
        <font-awesome-icon :icon="['fas', 'plus']" />New
      </fts-btn-hold>
      <!-- activities -->
      <fts-btn-hold
        v-for="activity in availableActivities"
        :key="activity.id"
        outline
        :color="activity.color ? activity.color : 'primary'"
        text-transform-init
        class="btn-activity"
        @clickEvent="executeActivity(activity,selectedEntries,)"
        @longPressEvent="executeActivity(activity, selectedEntries, true)"
      >
        <span class="btn-activity-label">{{ activity.name }}</span>
      </fts-btn-hold>
      <!-- documents -->
      <fts-btn
        v-for="document in availableDocuments"
        :key="document.id"
        outline
        color="ternary-green"
        text-transform-init
        class="btn-activity"
        @click="executeDocument(document)"
      >
        <font-awesome-icon
          :icon="['far', 'file-pdf']"
          class="btn-activity-icon fts-file-pdf"
          v-if="document.typeId === 'pdf'"
        />
        <font-awesome-icon
          :icon="['far', 'file-alt']"
          class="btn-activity-icon fts-file-excel"
          v-else-if="document.typeId === 'spreadsheet'"
        />
        <font-awesome-icon
          :icon="['far', 'file-code']"
          class="btn-activity-icon fts-file-html"
          v-else-if="document.typeId === 'html'"
        />
        <font-awesome-icon
          :icon="['far', 'file']"
          class="btn-activity-icon fts-file-default"
          v-else
        />
        <span class="btn-activity-label">{{ document.name }}</span>
      </fts-btn>
      <!-- customs -->
      <fts-btn
        v-for="custom in availableCustoms"
        :key="custom.id"
        outline
        :color="custom.color ? custom.color : 'ternary-blue'"
        :emoji="custom.emoji"
        text-transform-init
        class="btn-activity"
        @click="executeCustom(custom)"
      >
        <span :class="{ 'btn-activity-label': !custom.color }">{{custom.name}}</span>
      </fts-btn>
      <!-- bulk activities -->
      <fts-btn-hold
        v-for="activity in bulkActivities"
        :key="activity.id"
        outline
        :color="activity.id === 'delete' ? 'danger' : 'primary'"
        text-transform-init
        class="btn-activity"
        @clickEvent="executeActivity(activity, selectedEntries)"
        @longPressEvent="executeActivity(activity, selectedEntries, true)"
      >
        <i :class="activity.icon"></i> {{ activity.name }}
      </fts-btn-hold>
    </div>

    <fts-action-page-modal ref="ftsActionPageModal" />
    <fts-document-form-modal ref="ftsDocumentFormModal" />
    <fts-document-modal ref="ftsDocumentModal" />
  </div>
</template>

<script>
import commons from '../../commons/commons';
import { activityPortalId } from '../../portals/activityPortal';
import { entryStateId } from '../../commons/entryState';

import FTSActionPageModal from '../FTSActionPageModal';
import FTSBtn from '../FTSBtn';
import FTSBtnHold from '../FTSBtnHold';
import FTSDocumentFormModal from '../FTSDocumentFormModal';
import FTSDocumentModal from '../FTSDocumentModal';

const BULK_MAX_COUNT = 50;
const BULK_EXCEED_ERROR = `Selected items exceeded. Maximum only allows to select up to ${BULK_MAX_COUNT}.`;

export default {
  components: {
    "fts-action-page-modal": FTSActionPageModal,
    "fts-btn": FTSBtn,
    "fts-btn-hold": FTSBtnHold,
    "fts-document-form-modal": FTSDocumentFormModal,
    "fts-document-modal": FTSDocumentModal
  },
  props: {
    moduleId: [String, Number],
    listingId: String,
    standardActivities: Array,
    activities: Array,
    documents: Array,
    customs: Array,
    filters: Object,
    selectedEntries: Array,
    enableBulk: Boolean,
    // The portal Create opens instead of its form: the listing header's createPortalId (activityPortal.js).
    createPortalId: String
  },
  methods: {
    executeActivity(activity, selectedEntries, isFullView = false) {
      if (
        Array.isArray(selectedEntries) && 
        selectedEntries.length > BULK_MAX_COUNT
      ) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      // if (activity.skipConfirm && !activity.standard) {
      //   window.eventBus.$emit("formSkipConfirmation", {
      //     moduleId: this.moduleId && this.moduleId.toString(),
      //     activity: activity,
      //     activityId: activity.id,
      //     entryIds: selectedEntries.map(x => x.id)
      //   });

      //   if (this.enableBulk) {
      //     this.$emit("bulk");
      //   }
      // } else {
      window.eventBus.$emit("quickViewData", {
        items: selectedEntries,
        vectorId: this.moduleId,
        listingId: this.listingId,
        activityId: activity.id,
        // The portal page this activity opens instead of the form, if any.
        portalId: activityPortalId(activity, { createPortalId: this.createPortalId }),
        fullView: isFullView
      });
      // }
    },
    executeCreateActivity(isFullView = false) {
      this.executeActivity({ id: "create" }, [], isFullView);
    },
    async executeDocument(document) {
      if (
        Array.isArray(this.selectedEntries) && 
        this.selectedEntries.length > BULK_MAX_COUNT
      ) {
        this.$toasted.error(BULK_EXCEED_ERROR);
        return;
      }

      let vm = this;
      if (document.type === "template") {
        let template = {
          templateId: document.id,
          entryId: items[0].id
        };
        this.$refs.ftsDocumentModal.show(template);
      } else if (document.hasForm) {
        await this.$refs.ftsDocumentFormModal.show({
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: document,
          filters: this.filters,
          items: this.selectedEntries
        });
      } else {
        let parameters = {
          moduleId: this.moduleId,
          listingId: this.listingId,
          document: document,
          filters: this.filters,
          items: this.selectedEntries
        }

        if (document.dataSourceId === "logicService") {
          if (!parameters.inputs) {
            parameters.inputs = {};
          }
          
          if (
            Array.isArray(this.selectedEntries) && 
            this.selectedEntries.length > 0
          ) {
            parameters.inputs.id = this.selectedEntries[0].id;
            parameters.inputs.locusIds = this.selectedEntries.map(x => x.id);
          }
        }

        window.eventBus.$emit("showPDFDocument", parameters);
      }
    },
    executeCustom(custom) {
      commons.executeEntryCustom({
        action: custom,
        items: this.selectedEntries,
        modal: this.$refs.ftsActionPageModal
      });
    }
  },
  computed: {
    isCreateRight() {
      return Array.isArray(this.standardActivities) && this.standardActivities.includes("create");
    },
    getCreateActivityLink() {
      return commons.clientPathUrl({
        moduleId: this.moduleId,
        listId: this.listingId,
        activity: "create"
      });
    },
    availableActivities() {
      if (
        Array.isArray(this.activities) &&
        Array.isArray(this.selectedEntries) && 
        this.selectedEntries.length
      ) {
        return this.activities.filter(activity => {
          if (this.selectedEntries.length > 1 && activity.disableBulkActions) {
            return false;
          }

          if (activity.fromStates && activity.fromStates.length) {
            return this.selectedEntries.every((item) =>
              activity.fromStates.some((y) => y == entryStateId(item.state))
            );
          }

          return true;
        });
      }

      return [];
    },
    availableDocuments() {
      if (Array.isArray(this.documents)) {
        return this.documents.filter(document => {
          if (Array.isArray(this.selectedEntries) && this.selectedEntries.length) {
            if (this.selectedEntries.length > 1) {
              // Multi-select: legacy allowSelection docs, PLUS htmljs single-entry ("entry type")
              // documents — those are combined into ONE PDF across the selection (bulk print, §5d
              // Flavor A). Collection/report docs (dataSourceId "collection") are whole-list, so they
              // stay excluded on selection.
              return (
                document.allowSelection ||
                (document.engine === "htmljs" && document.dataSourceId !== "collection")
              );
            } else {
              return true;
            }
          }

          return (
            document.dataSourceId === "collection" || 
            document.dataSourceId === "logicService"
          );
        });
      }

      return [];
    },
    availableCustoms() {
      if (Array.isArray(this.customs)) {
        return this.customs.filter(custom => {
          if (Boolean(custom.condition)) {
            try {
              return Boolean(eval(custom.condition));
            } catch (err) {
              console.error('[Custom:Condition error]', err);
              return false;
            }
          }

          if (Array.isArray(this.selectedEntries) && this.selectedEntries.length) {
            if (this.selectedEntries.length > 1) {
              return custom.isMultiple;
            } else {
              return true;
            }
          }

          return custom.isDefault;
        });
      }

      return [];
    },
    bulkActivities() {
      if (
        this.enableBulk && 
        Array.isArray(this.selectedEntries) && 
        this.selectedEntries.length > 0 
      ) {
        let standardActivities = _.intersectionWith(
          commons.standardActivities.filter(x => x.bulk),
          this.standardActivities,
          ({ id }, key) => id === key
        );

        return standardActivities;
      }

      return [];
    }
  }
}
</script>

<style lang="scss">
@use "./src/assets/scss/vars" as *;

.fts-listing-actions {
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
  .listing-actions-container {
    @include flex-gap($gap: 0.575rem, $display: flex);
  }
}
</style>